/**
 * Weighted keyword list for slogan rotation on the plcy.io landing page.
 *
 * @remarks
 * Words are weighted by accuracy of describing an AI-powered insurance platform
 * combined with a positivity bonus. Higher weight = more frequent appearance.
 * "different." is the hero brand word and always appears first on page load.
 *
 * @packageDocumentation
 */

import type { WeightedKeyword } from '@plcy-io/app-core';

export const SLOGAN_KEYWORDS: readonly WeightedKeyword[] = [
  // Tier 1 — Hero (weight 8) — ~4.5% of appearances
  { text: 'different.', weight: 8 },

  // Tier 2 — Core Identity (weight 5) — ~2.8% each
  { text: 'intelligent.', weight: 5 },
  { text: 'effortless.', weight: 5 },
  { text: 'powerful.', weight: 5 },
  { text: 'smarter.', weight: 5 },
  { text: 'inspired.', weight: 5 },
  { text: 'automated.', weight: 5 },
  { text: 'innovative.', weight: 5 },
  { text: 'remarkable.', weight: 5 },

  // Tier 3 — Strong Fit (weight 3) — ~1.7% each
  { text: 'streamlined.', weight: 3 },
  { text: 'intuitive.', weight: 3 },
  { text: 'adaptive.', weight: 3 },
  { text: 'complete.', weight: 3 },
  { text: 'predictive.', weight: 3 },
  { text: 'proactive.', weight: 3 },
  { text: 'visionary.', weight: 3 },
  { text: 'dynamic.', weight: 3 },
  { text: 'efficient.', weight: 3 },
  { text: 'insightful.', weight: 3 },
  { text: 'secure.', weight: 3 },
  { text: 'scalable.', weight: 3 },
  { text: 'unified.', weight: 3 },
  { text: 'modern.', weight: 3 },
  { text: 'connected.', weight: 3 },
  { text: 'precise.', weight: 3 },
  { text: 'responsive.', weight: 3 },
  { text: 'reliable.', weight: 3 },
  { text: 'strategic.', weight: 3 },
  { text: 'transparent.', weight: 3 },

  // Tier 4 — Extended (weight 1) — all words ≤ 12 chars including period
  { text: 'brilliant.', weight: 1 },
  { text: 'elegant.', weight: 1 },
  { text: 'seamless.', weight: 1 },
  { text: 'empowering.', weight: 1 },
  { text: 'exceptional.', weight: 1 },
  { text: 'bold.', weight: 1 },
  { text: 'unstoppable.', weight: 1 },
  { text: 'decisive.', weight: 1 },
  { text: 'reimagined.', weight: 1 },
  { text: 'focused.', weight: 1 },
  { text: 'integrated.', weight: 1 },
  { text: 'trusted.', weight: 1 },
  { text: 'delightful.', weight: 1 },
  { text: 'purposeful.', weight: 1 },
  { text: 'engineered.', weight: 1 },
  { text: 'optimized.', weight: 1 },
  { text: 'calibrated.', weight: 1 },
  { text: 'evolved.', weight: 1 },
  { text: 'progressive.', weight: 1 },
  { text: 'advanced.', weight: 1 },
  { text: 'refined.', weight: 1 },
  { text: 'agile.', weight: 1 },
  { text: 'capable.', weight: 1 },
  { text: 'magnetic.', weight: 1 },
  { text: 'deliberate.', weight: 1 },
  { text: 'impactful.', weight: 1 },
  { text: 'compelling.', weight: 1 },
  { text: 'refreshing.', weight: 1 },
  { text: 'inventive.', weight: 1 },
  { text: 'astounding.', weight: 1 },
  { text: 'dependable.', weight: 1 },
  { text: 'robust.', weight: 1 },
  { text: 'thorough.', weight: 1 },
  { text: 'distinctive.', weight: 1 },
  { text: 'formidable.', weight: 1 },
  { text: 'flawless.', weight: 1 },
  { text: 'harmonious.', weight: 1 },
  { text: 'authentic.', weight: 1 },
  { text: 'superior.', weight: 1 },
  { text: 'enlightened.', weight: 1 },
  { text: 'immersive.', weight: 1 },
  { text: 'actionable.', weight: 1 },
  { text: 'meaningful.', weight: 1 },
  { text: 'thoughtful.', weight: 1 },
  { text: 'sustainable.', weight: 1 },
  { text: 'rapid.', weight: 1 },
  { text: 'lucid.', weight: 1 },
  { text: 'commanding.', weight: 1 },
  { text: 'versatile.', weight: 1 },
  { text: 'accessible.', weight: 1 },
  { text: 'resilient.', weight: 1 },
  { text: 'unmatched.', weight: 1 },
  { text: 'relentless.', weight: 1 },
  { text: 'liberating.', weight: 1 },
  { text: 'pioneering.', weight: 1 },
  { text: 'incredible.', weight: 1 },
  { text: 'elevating.', weight: 1 },
  { text: 'unrivaled.', weight: 1 },
  { text: 'boundless.', weight: 1 },
  { text: 'polished.', weight: 1 },
  { text: 'fearless.', weight: 1 },
  { text: 'limitless.', weight: 1 },
  { text: 'striking.', weight: 1 },
  { text: 'vivid.', weight: 1 },
  { text: 'stellar.', weight: 1 },
  { text: 'captivating.', weight: 1 },
  { text: 'refreshed.', weight: 1 },
  { text: 'undeniable.', weight: 1 },
  { text: 'monumental.', weight: 1 },
  { text: 'majestic.', weight: 1 },
  { text: 'luminous.', weight: 1 },
  { text: 'paramount.', weight: 1 },
  { text: 'magnificent.', weight: 1 },
  { text: 'wondrous.', weight: 1 },
  { text: 'spectacular.', weight: 1 },
  { text: 'triumphant.', weight: 1 },
  { text: 'dazzling.', weight: 1 },
  { text: 'splendid.', weight: 1 },
  { text: 'vibrant.', weight: 1 },
  { text: 'radiant.', weight: 1 },
  { text: 'potent.', weight: 1 },
  { text: 'vital.', weight: 1 },
  { text: 'crisp.', weight: 1 },
  { text: 'sharp.', weight: 1 },
  { text: 'savvy.', weight: 1 },
  { text: 'sleek.', weight: 1 },
  { text: 'swift.', weight: 1 },
  { text: 'grand.', weight: 1 },
  { text: 'fresh.', weight: 1 },
  { text: 'clever.', weight: 1 },
  { text: 'superb.', weight: 1 },
  { text: 'iconic.', weight: 1 },
  { text: 'electric.', weight: 1 },
  { text: 'thrilling.', weight: 1 },
  { text: 'essential.', weight: 1 },
  { text: 'enduring.', weight: 1 },
] as const;
